import React, { useMemo } from 'react';
import { useOrgMembers } from 'new/hooks/adapters/useOrgMembers';
import { useTeams } from 'new/hooks/adapters/useTeams';

import { TextField } from 'new/elements/TextField';
import { Autocomplete } from 'new/elements/Autocomplete';
import cx from 'classnames';
import styles from 'new/components/collaborate/Resources360/Resources360.module.scss';
import { AutocompleteRenderGroupParams, Typography } from '@mui/material';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import { useCurrentUser } from 'new/hooks/adapters/useCurrentUser';

interface AclEntitySelectProps {
  selected?: any[];
  label?: string;
  placeholder?: string;
  multiple?: boolean;
  removeGroups?: string[];
  onChangeSelection: (e: React.SyntheticEvent, value: any[]) => void;
}

const AutocompleteOption = (props, option) => {
  const caption = (() => {
    if (option.optype === 'USER') return option.full_name;
    if (option.optype === 'ORG') return option.label; // "Entire organization"
    if (option.optype === 'TEAM') return option.name;
  })();
  const body3 = (() => {
    if (option.optype === 'USER') return option.email;
    if (option.optype === 'ORG') return `Everyone in ${option.name} organization`;
    if (option.optype === 'TEAM') return option.description;
  })();
  return (
    <div
      {...props}
      className={cx(
        styles.autocompleteOptionContainer,
        props['aria-selected'] ? styles.autocompleteOptionContainer_selected : '',
      )}
    >
      <Typography variant="caption">{caption}</Typography>
      <Typography variant="body3">{body3}</Typography>
    </div>
  );
};

const renderGroup = ({ key, group, children }: AutocompleteRenderGroupParams) => {
  return (
    <div key={key}>
      <p className={styles.autocompleteGroup}>{group}</p>
      {children}
    </div>
  );
};

export const AclEntitySelect = (props: AclEntitySelectProps) => {
  const { selected, label, placeholder, onChangeSelection, removeGroups, multiple } = props;
  const org = useCurrentOrg();
  const user = useCurrentUser();
  const members = useOrgMembers({ onlyValid: true, orgId: org.id });
  const teams = useTeams();

  // Build options matching AclTabView.js logic:
  // - Single org option from current org (never fetch all orgs)
  // - Members from current org only
  // - Teams the user belongs to
  const items = useMemo(() => {
    const orgOption =
      removeGroups?.includes('orgs') || selected.some((a) => a.id === org.id)
        ? null
        : {
            id: org.id,
            name: org.name,
            label: 'Entire organization',
            optype: 'ORG',
          };

    const memberOptions = removeGroups?.includes('members')
      ? []
      : members
          .filter(
            (m) =>
              !selected.some((a) => a.id === m.id) &&
              m.id !== user.id &&
              m.email !== 'admin@nexla.com',
          )
          .map((m) => ({ ...m, label: m.full_name, optype: 'USER' }));

    const teamOptions = removeGroups?.includes('teams')
      ? []
      : teams
          .filter((t) => !selected.some((a) => a.id === t.id))
          .map((t) => ({ ...t, label: t.name, optype: 'TEAM' }));

    return [...(orgOption ? [orgOption] : []), ...memberOptions, ...teamOptions];
  }, [org, members, teams, selected, removeGroups, user.id]);

  return (
    <Autocomplete
      multiple={multiple}
      size="small"
      sx={{
        width: 300,
      }}
      groupBy={(option) => option.optype}
      renderGroup={renderGroup}
      getOptionLabel={(option) => {
        if (option.optype === 'USER') return option.full_name;
        if (option.optype === 'ORG') return option.label; // "Entire organization"
        return option.name;
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      disableClearable
      onChange={onChangeSelection}
      renderOption={AutocompleteOption}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder || 'Search users, teams or orgs'}
        />
      )}
      options={items}
    />
  );
};

AclEntitySelect.defaultProps = {
  selected: [],
  multiple: true,
};
